/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { useAuth0 } from '@auth0/auth0-react'
import { t } from '@lingui/macro'
import { Button, CircularProgress, Typography } from '@mui/material'
import { OpenAPI } from '@om1/aspen-api/codegen/client'
import { CandidateIndexDTO } from '@om1/aspen-api/codegen/client/models/CandidateIndexDTO'
import { useCandidates } from '@om1/aspen-api/codegen/hooks/useCandidates'
import { usePatientManagers } from '@om1/aspen-api/codegen/hooks/usePatientManagers'
import { useStatusOptions } from '@om1/aspen-api/codegen/hooks/useStatusOptions'
import { useListUrlState } from '../hooks/useListUrlState'
import { InlineDropdown, InlineDropdownOption } from '@om1/platform-components'
import OmniDataGridTable, { ProColumns } from '@om1/platform-components/OmniDataGridTable'
import { Tag } from 'antd'
import DOMPurify from 'dompurify'
import { Download, MessageSquare } from 'lucide-react'
import { useSnackbar } from 'notistack'
import React, { useCallback, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { AppointmentActions } from '../components/AppointmentActions'
import { CandidateFilterComponent, CandidateFilters } from '../components/CandidateFilterComponent'
import { CopyableIdentifier } from '../components/CopyableIdentifier'
import { DownloadCalendarAction } from '../components/DownloadCalendar'
import { FilterIconButton } from '../components/FilterIconButton'
import { DrawerComponent } from '../components/GlobalDrawer'
import InputTextModal from '../components/InputTextModal'
import LogAttemptAction from '../components/LogAttemptAction'
import { PatientManagerDropdown } from '../components/PatientManagerDropdown'
import PatientManagerFilter from '../components/PatientManagerFilter'
import { StatusBadge } from '../components/StatusBadge'
import { useUpdateCandidateStatus } from '../hooks/useUpdateCandidateStatus'
import { APPOINTMENT_STATUS_COLORS, APPOINTMENT_STATUS_LABELS, formatAppointmentSlot } from '../utils/appointment'
import { filterAllocatedStatusOptions } from '../utils/candidate-status'
import { calculateAge } from '../utils/format-date'

/**
 * Sanitizes text content to prevent XSS attacks using DOMPurify
 * @param text - The text to sanitize
 * @returns Sanitized text safe for display
 */
const sanitizeText = (text: string | undefined | null): string => {
    if (!text) return ''
    return DOMPurify.sanitize(String(text), { ALLOWED_TAGS: [] })
}

const formatPreferredContactTimeRange = (startValue?: string | null, endValue?: string | null): string | null => {
    if (!startValue && !endValue) return null
    const startDate = startValue ? new Date(startValue) : null
    const endDate = endValue ? new Date(endValue) : null

    const formatMonthDay = (date: Date) =>
        date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        })
    const formatYear = (date: Date) =>
        date.toLocaleDateString('en-US', {
            year: 'numeric'
        })
    const formatTime = (date: Date) =>
        date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        })

    if (startDate && endDate) {
        const sameYear = startDate.getFullYear() === endDate.getFullYear()
        const sameMonth = startDate.getMonth() === endDate.getMonth()
        const sameDay = startDate.toDateString() === endDate.toDateString()

        if (sameDay) {
            return `${formatMonthDay(startDate)} (${formatYear(startDate)}) ${formatTime(startDate)} - ${formatTime(endDate)}`
        }

        if (sameYear) {
            const dateRange = sameMonth
                ? `${formatMonthDay(startDate)}-${endDate.getDate()}`
                : `${formatMonthDay(startDate)} - ${formatMonthDay(endDate)}`
            return `${dateRange} (${formatYear(startDate)}) ${formatTime(startDate)} - ${formatTime(endDate)}`
        }

        return `${formatMonthDay(startDate)} ${formatYear(startDate)} - ${formatMonthDay(endDate)} ${formatYear(endDate)} ${formatTime(
            startDate
        )} - ${formatTime(endDate)}`
    }

    const singleDate = startDate ?? endDate
    if (!singleDate) return null
    return `${formatMonthDay(singleDate)} (${formatYear(singleDate)}) ${formatTime(singleDate)}`
}

// Flexible column configuration with optional sub-values .

// Any column can now display additional information below its main value

// Create a combined search and filter component

/**
 * Get all available candidate status options with labels
 */
const getCandidateStatusOptions = (): InlineDropdownOption<string>[] => [
    { value: 'candidate', label: t`Candidate` },
    { value: 'in_contact', label: t`In Contact` },
    { value: 'registered', label: t`Registered` },
    { value: 'allocated', label: t`Allocated` },
    { value: 'allocated_group_2', label: t`Allocated Group 2` },
    { value: 'allocated_group_3', label: t`Allocated Group 3` },
    { value: 'record_retrieval', label: t`Record Retrieval` },
    { value: 'screen_failure', label: t`Screen Failure` },
    { value: 'undecided', label: t`Undecided` },
    { value: 'declined', label: t`Declined` },
    { value: 'unreachable', label: t`Unreachable` },
    { value: 'duplicate', label: t`Duplicate` }
]

// Known referrer_id → source-name mapping (mirrors CandidateReferrerWidget).
// Returns the mapped, human-readable name, the raw id when unmapped, or a
// "No referrer" label when empty/unknown.
const resolveReferrerLabel = (referrerId?: string | null): string => {
    if (!referrerId || referrerId === 'unknown') return t`No referrer`
    const labels: Record<string, string> = {
        e958419d1b01005803a185bdfd6f6733974b4c96a0e5592cbe03a6fd1874bed3: t`PINC AI`,
        '9781fafbd71d05aa1ab51e61585c6ccd5ca3aea0e1bbb72c0349f95c44ccefb7': t`IQVIA`,
        om1: t`OM1`,
        neurodiscovery: t`Neuro Discovery`
    }
    return labels[referrerId] || referrerId
}

const CandidatesPageContent: React.FC<{ defaultStatuses: string[] }> = ({ defaultStatuses }) => {
    // The study slug the scheduler API expects is the Auth0 ORGANISATION name,
    // and it is safe to take the coordinator's own: RLS scopes this list to the
    // token's organisation, so every candidate shown necessarily belongs to it.
    // (Deriving it per-candidate is not possible — CandidateDTO carries
    // organizationId, an org_… id, not the name the /slots endpoint keys on.)
    const { user } = useAuth0()
    const studySlug = (user?.org_name as string | undefined) ?? ''

    const history = useHistory()
    const location = useLocation()

    // Referrer pre-applied from the dashboard "Candidates by Referrer" widget.
    // ``referrer`` is the id we filter by exactly (matches ``referrer_id`` on
    // the backend); ``referrerName`` is the mapped, human-readable label we
    // show in the search box (falling back to the raw id when unmapped).
    const referrerIdFromUrl = useMemo(() => {
        const value = new URLSearchParams(location.search).get('referrer')
        return value && value.length > 0 ? value : null
    }, [location.search])
    const referrerNameFromUrl = useMemo(() => {
        const value = new URLSearchParams(location.search).get('referrerName')
        return value && value.length > 0 ? value : null
    }, [location.search])
    const { updateCandidateStatus } = useUpdateCandidateStatus()
    // The active referrer (id + display label). Initialised synchronously from
    // the URL so the very first candidates fetch is already filtered (avoids a
    // race where a second, unfiltered request overwrites the filtered one).
    // Cleared when the user empties the search box.
    const [activeReferrer, setActiveReferrer] = useState<{ id: string; label: string } | null>(() =>
        referrerIdFromUrl ? { id: referrerIdFromUrl, label: referrerNameFromUrl || referrerIdFromUrl } : null
    )
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>([])
    const [reasonModalOpen, setReasonModalOpen] = useState(false)
    const [reasonText, setReasonText] = useState('')
    const [pendingStatus, setPendingStatus] = useState<string | null>(null)
    const [pendingCandidateId, setPendingCandidateId] = useState<string | null>(null)
    // Memoize params to prevent infinite re-renders
    const patientManagersParams = React.useMemo(
        () => ({
            page: 1,
            limit: 100,
            active: true,
            sort: 'name:asc'
        }),
        []
    )

    // Fetch patient managers once at page level
    const { patientManagers: fetchedManagers, loading: managersLoading, error: managersError } = usePatientManagers(patientManagersParams)

    // Sanitize patient manager data to prevent XSS attacks before passing to components
    const patientManagers = React.useMemo(() => {
        return fetchedManagers.map((manager) => ({
            ...manager,
            name: sanitizeText(manager.name),
            email: sanitizeText(manager.email)
        }))
    }, [fetchedManagers])

    // Create initial filters for candidates — include the referrer up front so
    // the first request is filtered (no unfiltered call to race against).
    //
    // Note: no studyName filter. Organization scoping is handled by RLS, and
    // the candidates endpoint has no such parameter — it was being sent and
    // silently ignored. Keeping it out also keeps the org identifier out of
    // shareable URLs.
    const initialCandidatesFilters = useMemo(
        () => ({
            // Open on work that needs attention rather than every candidate in
            // the study. Comma-joined because the filter map is
            // Record<string, string>; useCandidates expands it per request.
            ...(defaultStatuses.length > 0 ? { status: defaultStatuses.join(',') } : {}),
            ...(activeReferrer ? { referrerId: activeReferrer.id } : {})
        }),
        // Only the first render's value is used by the hook; intentionally
        // excluding activeReferrer so later changes go through setFilters below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [defaultStatuses]
    )

    // List state lives in the URL (layered over localStorage) so clicking into
    // a candidate and coming back — or sharing the link — preserves it.
    const listState = useListUrlState('candidates', {
        page: 1,
        limit: 10,
        sort: 'first_name:asc',
        search: '',
        filters: initialCandidatesFilters
    })

    // Fetch candidates data
    const candidatesData = useCandidates(1, 10, 'first_name:asc', initialCandidatesFilters, listState)
    const { filters: candidateFilters, setFilters: setCandidateFilters } = candidatesData

    // Derived from the filter map rather than mirrored in local state: local
    // state dies when the page unmounts on the way to a candidate, so the
    // filter would survive in the URL while the dropdown reset to "All".
    const selectedPatientManager = candidateFilters?.patientManagerId ?? null

    const handlePatientManagerChange = useCallback(
        (id: string | null) => {
            setCandidateFilters((previousFilters) => {
                const nextFilters = { ...previousFilters }
                if (id) {
                    nextFilters.patientManagerId = id
                } else {
                    delete nextFilters.patientManagerId
                }
                return nextFilters
            })
        },
        [setCandidateFilters]
    )

    // Update filters when the active referrer changes. Skipped on first mount
    // because ``initialCandidatesFilters`` already applied it — this prevents a
    // duplicate fetch on load. The patient manager writes straight to the
    // filters in its own handler, so it needs no sync here.
    const didMountRef = React.useRef(false)
    React.useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true
            return
        }

        candidatesData.setFilters((previousFilters) => {
            const nextFilters: Record<string, string> = {
                ...previousFilters
            }

            if (activeReferrer) {
                nextFilters.referrerId = activeReferrer.id
            } else {
                delete nextFilters.referrerId
            }

            return nextFilters
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeReferrer])

    const applyCandidateFilters = useCallback(
        (newFilters: CandidateFilters) => {
            const updatedFilters: Record<string, string> = {
                ...candidateFilters
            }

            // ``filters`` is Record<string, string>; useCandidates splits this
            // back into repeated ?status= params on the request.
            if (newFilters.status && newFilters.status.length > 0) {
                updatedFilters.status = newFilters.status.join(',')
            } else {
                delete updatedFilters.status
            }

            if (newFilters.createdAfter) {
                updatedFilters.createdAfter = newFilters.createdAfter
            } else {
                delete updatedFilters.createdAfter
            }

            if (newFilters.createdBefore) {
                updatedFilters.createdBefore = newFilters.createdBefore
            } else {
                delete updatedFilters.createdBefore
            }

            if (newFilters.uncontactedOnly) {
                updatedFilters.uncontactedOnly = 'true'
            } else {
                delete updatedFilters.uncontactedOnly
            }

            if (newFilters.hasPreferredContactTime === true) {
                updatedFilters.hasPreferredContactTime = 'true'
            } else if (newFilters.hasPreferredContactTime === false) {
                updatedFilters.hasPreferredContactTime = 'false'
            } else {
                delete updatedFilters.hasPreferredContactTime
            }

            if (newFilters.preferredContactAfter) {
                updatedFilters.preferredContactAfter = newFilters.preferredContactAfter
            } else {
                delete updatedFilters.preferredContactAfter
            }

            if (newFilters.preferredContactBefore) {
                updatedFilters.preferredContactBefore = newFilters.preferredContactBefore
            } else {
                delete updatedFilters.preferredContactBefore
            }

            // Appointment filters (APP-1220). The shared paginated hook stores
            // filters as flat strings, so the status list is comma-joined here
            // and expanded again in useCandidates.
            if (newFilters.appointmentAfter) {
                updatedFilters.appointmentAfter = newFilters.appointmentAfter
            } else {
                delete updatedFilters.appointmentAfter
            }

            if (newFilters.appointmentBefore) {
                updatedFilters.appointmentBefore = newFilters.appointmentBefore
            } else {
                delete updatedFilters.appointmentBefore
            }

            if (newFilters.appointmentStatus && newFilters.appointmentStatus.length > 0) {
                updatedFilters.appointmentStatus = newFilters.appointmentStatus.join(',')
            } else {
                delete updatedFilters.appointmentStatus
            }

            // Explicit `=== false` rather than a falsy check: "no appointment
            // yet" is a filter in its own right, and the truthy shorthand used
            // for `uncontactedOnly` above would drop it.
            if (newFilters.hasAppointment === true) {
                updatedFilters.hasAppointment = 'true'
            } else if (newFilters.hasAppointment === false) {
                updatedFilters.hasAppointment = 'false'
            } else {
                delete updatedFilters.hasAppointment
            }

            setCandidateFilters(updatedFilters)
        },
        [candidateFilters, setCandidateFilters]
    )

    // Clearing the search box also drops the active referrer (so the list
    // resets to all candidates); any other value is a normal free-text search.
    const handleSearchChange = (value: string) => {
        if (!value && activeReferrer) {
            setActiveReferrer(null)
        }
        candidatesData.setSearch(value)
    }

    // Check if any filters are active
    const hasActiveFilters = useMemo(() => {
        return Boolean(
            selectedPatientManager !== null ||
                candidatesData.filters?.referrerId ||
                candidatesData.filters?.status ||
                candidatesData.filters?.createdAfter ||
                candidatesData.filters?.createdBefore ||
                candidatesData.filters?.uncontactedOnly === 'true' ||
                candidatesData.filters?.hasPreferredContactTime === 'true' ||
                candidatesData.filters?.hasPreferredContactTime === 'false' ||
                candidatesData.filters?.preferredContactAfter ||
                candidatesData.filters?.preferredContactBefore ||
                (candidatesData.search && candidatesData.search.length > 0)
        )
    }, [selectedPatientManager, candidatesData.filters, candidatesData.search])

    // CSV Export
    const { enqueueSnackbar } = useSnackbar()
    const [isExporting, setIsExporting] = useState(false)

    const handleExportCsv = useCallback(async () => {
        setIsExporting(true)
        try {
            const params = new URLSearchParams()
            const filters = candidatesData.filters || {}

            if (filters.referrerId) params.append('referrer_id', filters.referrerId)
            // Repeat the param per status so the CSV matches the worklist.
            if (filters.status) {
                filters.status
                    .split(',')
                    .filter(Boolean)
                    .forEach((status) => params.append('status', status))
            }
            if (filters.createdAfter) params.append('created_after', filters.createdAfter)
            if (filters.createdBefore) params.append('created_before', filters.createdBefore)
            if (filters.uncontactedOnly === 'true') params.append('uncontacted_only', 'true')
            if (filters.hasPreferredContactTime === 'true') params.append('has_preferred_contact_time', 'true')
            if (filters.hasPreferredContactTime === 'false') params.append('has_preferred_contact_time', 'false')
            if (filters.preferredContactAfter) params.append('preferred_contact_after', filters.preferredContactAfter)
            if (filters.preferredContactBefore) params.append('preferred_contact_before', filters.preferredContactBefore)
            if (filters.appointmentAfter) params.append('appointment_after', filters.appointmentAfter)
            if (filters.appointmentBefore) params.append('appointment_before', filters.appointmentBefore)
            // Repeated rather than comma-joined: the endpoint takes a list, and
            // a comma-joined value would be read as one bogus status.
            if (filters.appointmentStatus) {
                filters.appointmentStatus
                    .split(',')
                    .filter(Boolean)
                    .forEach((status) => params.append('appointment_status', status))
            }
            if (filters.patientManagerId) params.append('patient_manager_id', filters.patientManagerId)
            if (filters.carePartnerId) params.append('care_partner_id', filters.carePartnerId)
            if (candidatesData.search) params.append('search', candidatesData.search)
            if (candidatesData.sort) params.append('sort', candidatesData.sort)

            const queryString = params.toString()
            const url = `${OpenAPI.BASE}/aspen/candidates/export${queryString ? `?${queryString}` : ''}`

            const token = typeof OpenAPI.TOKEN === 'string' ? OpenAPI.TOKEN : await OpenAPI.TOKEN?.({ method: 'GET', url })
            const response = await fetch(url, {
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                }
            })

            if (!response.ok) {
                throw new Error(`Export failed: ${response.statusText}`)
            }

            const blob = await response.blob()
            const contentDisposition = response.headers.get('content-disposition')
            const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/)
            const filename = filenameMatch?.[1] || `candidate_export_${new Date().toISOString().slice(0, 10)}.csv`

            const a = document.createElement('a')
            a.href = URL.createObjectURL(blob)
            a.download = filename
            a.click()
            URL.revokeObjectURL(a.href)

            enqueueSnackbar(t`CSV export downloaded successfully`, { variant: 'success' })
        } catch (error) {
            console.error('CSV export failed:', error)
            enqueueSnackbar(t`Failed to export CSV. Please try again.`, { variant: 'error' })
        } finally {
            setIsExporting(false)
        }
    }, [candidatesData.filters, candidatesData.search, candidatesData.sort, enqueueSnackbar])

    // Define ProColumns for the candidates data grid
    const proTableCandidateColumns: ProColumns<CandidateIndexDTO>[] = [
        {
            title: t`Identifier`,
            dataIndex: 'friendlyId',
            key: 'friendlyId',
            width: 150,
            render: (_: unknown, candidate: CandidateIndexDTO) => <CopyableIdentifier identifier={candidate.friendlyId} />
        },
        {
            title: t`First Name`,
            dataIndex: 'firstName',
            key: 'firstName',
            width: 150,
            sorter: true,
            render: (text, candidate) => (
                <div className='flex flex-col gap-1'>
                    <button
                        type='button'
                        className='font-medium text-primary hover:underline'
                        onClick={() => history.push(`/candidates/${candidate.id}`)}
                    >
                        {text}
                    </button>
                </div>
            )
        },
        {
            title: t`Last Name`,
            dataIndex: 'lastName',
            key: 'lastName',
            width: 150,
            sorter: true,
            render: (text, candidate) => (
                <button
                    type='button'
                    className='font-medium text-primary hover:underline'
                    onClick={() => history.push(`/candidates/${candidate.id}`)}
                >
                    {text}
                </button>
            )
        },
        {
            title: t`Date of Birth`,
            dataIndex: 'dateOfBirth',
            key: 'dateOfBirth',
            width: 120,
            sorter: true,
            // APP-1717: age sits under the date of birth so the outreach team can
            // see at a glance whether to ring the patient or a parent/guardian.
            // Not NeuroPace-specific -- every study collecting a date of birth
            // gets it. Hidden rather than guessed when the date is missing or
            // unparseable; see calculateAge.
            render: (text, candidate) => {
                const age = calculateAge(candidate.dateOfBirth)
                return (
                    <div className='flex flex-col gap-1'>
                        <span>{text || '-'}</span>
                        {age !== null && <span className='text-xs text-gray-500'>{t`Age ${age}`}</span>}
                    </div>
                )
            }
        },
        {
            // APP-1220: was "Preferred Contact Time" — a two-hour window the
            // patient had *asked for*. This shows the real booking, in the
            // PATIENT's timezone with the zone labelled, because the previous
            // formatter used the browser's zone and so silently misreported a
            // Pacific patient's slot by three hours to an Eastern coordinator.
            title: t`Appointment`,
            dataIndex: 'appointment',
            key: 'appointmentStartAt',
            width: 260,
            sorter: true,
            render: (_, candidate) => {
                const appointment = candidate.appointment
                if (!appointment) {
                    // Falls back to the legacy window rather than showing a dash:
                    // candidates who registered before the scheduler still have
                    // one, and APP-1222 has not backfilled them yet.
                    const legacy = formatPreferredContactTimeRange(candidate.preferredContactStartDatetime, candidate.preferredContactEndDatetime)
                    return legacy ? <span className='text-gray-500'>{legacy} (preference)</span> : '-'
                }
                const slot = formatAppointmentSlot(appointment, candidate.timezone)
                return (
                    <div className='flex flex-col gap-1'>
                        <span>{slot ?? '-'}</span>
                        <div className='flex items-center gap-2'>
                            <Tag color={APPOINTMENT_STATUS_COLORS[appointment.status]} className='m-0'>
                                {APPOINTMENT_STATUS_LABELS[appointment.status]}
                            </Tag>
                            {appointment.hostEmail && <span className='text-xs text-gray-500'>{appointment.hostEmail}</span>}
                        </div>
                    </div>
                )
            }
        },
        {
            title: t`Phone`,
            dataIndex: 'phone',
            key: 'phone',
            width: 140,
            render: (text, candidate) => (
                <div className='flex flex-col gap-1'>
                    <span className='flex items-center gap-1'>
                        {text} {candidate.smsConsent && <MessageSquare className='w-3.5 h-3.5 text-green-600' />}
                    </span>
                    {/* Language Preference */}
                    <div className='flex items-center gap-1 text-[10px] leading-none mt-1'>
                        <span className='text-gray-600'>{t`Lang:`}</span>
                        <span
                            className={`px-1 py-0.5 rounded text-[6px] font-medium ${
                                candidate.languagePreference === 'es' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                            }`}
                        >
                            {candidate.languagePreference === 'es' ? 'ES' : 'EN'}
                        </span>
                    </div>
                </div>
            )
        },
        {
            title: t`Email`,
            dataIndex: 'email',
            key: 'email',
            width: 200
        },
        {
            title: t`Referrer`,
            dataIndex: 'referrerId',
            key: 'referrer',
            width: 160,
            render: (_, candidate) => resolveReferrerLabel(candidate.referrerId)
        },
        {
            title: t`Created At`,
            dataIndex: 'createdDttm',
            key: 'createdDttm',
            width: 150,
            sorter: true,
            render: (text, candidate) => {
                if (!candidate.createdDttm) return '-'
                const date = new Date(candidate.createdDttm)
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                })
            }
        },
        {
            title: t`Updated At`,
            dataIndex: 'updatedDttm',
            key: 'updatedDttm',
            width: 150,
            sorter: true,
            render: (text, candidate) => {
                if (!candidate.updatedDttm) return '-'
                const date = new Date(candidate.updatedDttm)
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                })
            }
        },
        {
            title: t`Status`,
            key: 'status',
            width: 180,
            render: (_, candidate) => (
                <>
                    <InlineDropdown
                        value={candidate.status}
                        options={filterAllocatedStatusOptions(getCandidateStatusOptions(), studySlug)}
                        onSelect={async (newStatus) => {
                            if (newStatus === 'declined' || newStatus === 'screen_failure') {
                                setPendingCandidateId(candidate.id)
                                setPendingStatus(newStatus)
                                setReasonText('')
                                setReasonModalOpen(true)
                                return
                            }
                            setPendingCandidateId(null)
                            setPendingStatus(null)
                            await updateCandidateStatus(candidate.id, newStatus, null)

                            candidatesData.refetch?.()
                        }}
                        renderTrigger={(value, isOpen) => (
                            <div className='inline-flex items-center cursor-pointer hover:opacity-80'>
                                <StatusBadge status={value} />
                                <span className={`ml-1 text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}>▼</span>
                            </div>
                        )}
                    />
                </>
            )
        },
        {
            title: t`Decline Reason`,
            dataIndex: 'declineReasonText',
            key: 'declineReason',
            width: 200,
            render: (text, candidate) => {
                if (candidate.status !== 'declined') return '-'
                return text || '-'
            }
        },
        {
            title: t`Screen Failure Reason`,
            dataIndex: 'screenFailureReason',
            key: 'screenFailureReason',
            width: 200,
            render: (text, candidate) => {
                if (candidate.status !== 'screen_failure') return '-'
                return text || '-'
            }
        },
        {
            title: t`Calendar`,
            key: 'calendar',
            width: 100,
            align: 'center',
            render: (_, candidate) => <DownloadCalendarAction candidate={candidate} onDownloaded={() => candidatesData.refetch?.()} />
        },
        {
            // APP-1221. Renders nothing without manage:candidates, and nothing
            // for an appointment that cannot be acted on upstream — so the
            // column is empty rather than misleading for legacy rows.
            title: t`Appointment Actions`,
            key: 'appointmentActions',
            width: 140,
            render: (_, candidate) => <AppointmentActions candidate={candidate} study={studySlug} onChanged={() => candidatesData.refetch?.()} />
        },
        {
            title: t`Patient Manager`,
            key: 'patientManager',
            width: 200,
            render: (_, candidate) => (
                <PatientManagerDropdown
                    currentManagerId={candidate.patientManagerId}
                    entityId={candidate.id}
                    entityType='candidate'
                    patientManagers={patientManagers}
                    loading={managersLoading}
                    onAssignmentComplete={() => candidatesData.refetch?.()}
                />
            )
        },
        {
            title: t`Log Attempt`,
            key: 'logAttempt',
            width: 120,
            align: 'center',
            render: (_, candidate) => (
                <div className='flex items-center justify-center gap-2'>
                    <LogAttemptAction entityName={`${candidate.firstName} ${candidate.lastName}`} entityId={candidate.id} entityType='candidate' />
                    <span className='font-medium text-gray-900'>{candidate.contactLogCount || 0}</span>
                </div>
            )
        },
        {
            title: t`Actions`,
            key: 'actions',
            width: 100,
            align: 'center',

            render: (_, candidate) => (
                <Button variant='outlined' size='small' onClick={() => history.push(`/candidates/${candidate.id}`)}>
                    {t`View`}
                </Button>
            )
        }
    ]

    // Render Candidates view
    return (
        <div className='h-[90vh] flex flex-col'>
            {/* Header */}
            <div className='flex-shrink-0 p-6 pb-4 border-b'>
                <div className='flex justify-between items-center'>
                    <Typography variant='h4'>{t`Candidate Worklist`}</Typography>

                    <div className='mb-3'>
                        <div className='flex items-center gap-4 flex-wrap'>
                            <Button
                                variant='outlined'
                                size='small'
                                startIcon={isExporting ? <CircularProgress size={16} /> : <Download size={16} />}
                                onClick={handleExportCsv}
                                disabled={isExporting}
                            >
                                {isExporting ? t`Exporting...` : t`Export CSV`}
                            </Button>
                            <FilterIconButton onClick={() => setIsDrawerOpen(true)} hasActiveFilters={hasActiveFilters} />
                        </div>
                    </div>
                </div>

                {/* Bulk Actions Bar */}
                {selectedCandidateIds.length > 0 && (
                    <div className='mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between'>
                        <Typography variant='body2' className='text-blue-800'>
                            {t`${selectedCandidateIds.length} candidate(s) selected`}
                        </Typography>
                        <div className='flex items-center gap-3'>
                            <PatientManagerDropdown
                                selectedEntityIds={selectedCandidateIds}
                                entityType='candidate'
                                patientManagers={patientManagers}
                                loading={managersLoading}
                                onAssignmentComplete={() => {
                                    candidatesData.refetch?.()
                                    setSelectedCandidateIds([])
                                }}
                                variant='button'
                                bulkTriggerLabel={t`Assign Patient Manager`}
                            />
                        </div>
                    </div>
                )}

                {/* Data Grid */}
                <div className='pb-12'>
                    <OmniDataGridTable<CandidateIndexDTO>
                        dataSource={candidatesData.response?.data || []}
                        columns={proTableCandidateColumns}
                        loading={candidatesData.loading}
                        currentPage={candidatesData.page}
                        pageSize={candidatesData.limit}
                        enableRowSelection={true}
                        onSelectionChange={(selectedRowKeys, selectedRows) => {
                            setSelectedCandidateIds(selectedRowKeys as string[])
                        }}
                        total={candidatesData.response?.meta?.totalItems || 0}
                        onPaginationChange={(page, pageSize) => {
                            candidatesData.setPage(page)
                            candidatesData.setLimit(pageSize)
                        }}
                        onSortChange={(sortString) => {
                            candidatesData.setSort(sortString ?? '')
                        }}
                        // The Appointment column's dataIndex is 'appointment',
                        // which camelToSnake would send as 'appointment' -- a
                        // field PatientCandidate does not have, so the request
                        // 500s. The appointment is its own table; the API sorts
                        // it under this name (APP-1220).
                        fieldNameMap={{ appointment: 'appointment_start_at' }}
                        rowKey='id'
                        enableColumnStatePersistence={true}
                        storageKey='candidates-list'
                    />
                </div>
            </div>

            {/* Global Drawer for Filters */}
            <DrawerComponent open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title={t`Filters`} width={420}>
                <div className='flex flex-col gap-6 p-4'>
                    {/* Patient Manager Filter */}
                    <div className='flex flex-col gap-2'>
                        <Typography variant='subtitle2' className='font-semibold'>
                            {t`Patient Manager`}
                        </Typography>
                        {!managersLoading && (
                            <PatientManagerFilter
                                value={selectedPatientManager}
                                onChange={handlePatientManagerChange}
                                size='small'
                                showAllOption={true}
                                showActiveOnly={true}
                                patientManagers={patientManagers}
                                loading={managersLoading}
                                error={managersError}
                            />
                        )}
                    </div>

                    {/* Divider */}
                    <div className='border-t border-gray-200' />

                    {/* Candidate Filters */}
                    <CandidateFilterComponent
                        isOpen={isDrawerOpen}
                        onFiltersChange={applyCandidateFilters}
                        currentFilters={{
                            search: candidatesData.search,
                            status: candidatesData.filters?.status ? candidatesData.filters.status.split(',').filter(Boolean) : undefined,
                            createdAfter: candidatesData.filters?.createdAfter,
                            createdBefore: candidatesData.filters?.createdBefore,
                            uncontactedOnly: candidatesData.filters?.uncontactedOnly === 'true',
                            hasPreferredContactTime:
                                candidatesData.filters?.hasPreferredContactTime === 'true'
                                    ? true
                                    : candidatesData.filters?.hasPreferredContactTime === 'false'
                                    ? false
                                    : undefined,
                            appointmentAfter: candidatesData.filters?.appointmentAfter,
                            appointmentBefore: candidatesData.filters?.appointmentBefore,
                            appointmentStatus: candidatesData.filters?.appointmentStatus?.split(',').filter(Boolean),
                            hasAppointment:
                                candidatesData.filters?.hasAppointment === 'true'
                                    ? true
                                    : candidatesData.filters?.hasAppointment === 'false'
                                    ? false
                                    : undefined,
                            preferredContactAfter: candidatesData.filters?.preferredContactAfter,
                            preferredContactBefore: candidatesData.filters?.preferredContactBefore
                        }}
                        onSearchChange={handleSearchChange}
                        currentSearch={candidatesData.search || activeReferrer?.label || ''}
                    />
                </div>
            </DrawerComponent>

            <InputTextModal
                open={reasonModalOpen}
                title={t`Reason`}
                value={reasonText}
                onChange={setReasonText}
                onCancel={() => {
                    setReasonModalOpen(false)
                    setPendingCandidateId(null)
                    setPendingStatus(null)
                    setReasonText('')
                }}
                onSave={async () => {
                    if (!pendingCandidateId || !pendingStatus) return
                    await updateCandidateStatus(pendingCandidateId, pendingStatus, reasonText ? (reasonText as unknown as string) : null)
                    setReasonModalOpen(false)
                    setPendingCandidateId(null)
                    setPendingStatus(null)
                    setReasonText('')
                    candidatesData.refetch?.()
                }}
                placeholder={t`Enter reason`}
                saveText={t`Save`}
                cancelText={t`Cancel`}
            />
        </div>
    )
}

/**
 * The worklist opens on the statuses that need coordinator attention, so the
 * first request has to carry them already. ``useStatusOptions`` is async, so
 * the list is held back until the actionable set is known — applying it after
 * mount would mean an initial fetch of every candidate in the study, instantly
 * replaced, which is the load this change exists to remove.
 */
const CandidatesPage: React.FC = () => {
    const { actionableStatuses, loading: statusOptionsLoading } = useStatusOptions()

    if (statusOptionsLoading) {
        return (
            <div className='flex h-64 items-center justify-center' data-testid='candidates-status-options-loading'>
                <CircularProgress />
            </div>
        )
    }
    return <CandidatesPageContent defaultStatuses={actionableStatuses} />
}

export default CandidatesPage
